import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Collapse } from '@/components/ui/collapse'
import { Divider } from '@/components/ui/divider'
import { FileItem } from '@/components/ui/file-item'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { toastFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { CommunicationTypeEnum } from '@/models'
import { getFileType } from '@/utils/file/get-file-type'

import * as S from './announcement-details-panel.styled'
import { AnnouncementDetailsPanelData } from './announcement-details-panel.types'
import { getAnnouncementDetailsPanelFields } from './announcement-details-panel.utils'

export interface AnnouncementDetailsPanelProps {
  data: AnnouncementDetailsPanelData
}

export const AnnouncementDetailsPanel = ({ data }: AnnouncementDetailsPanelProps) => {
  const { t } = useTranslation(['communication'])
  const [isBodyExpanded, setIsBodyExpanded] = useState(false)
  const [showBodyToggle, setShowBodyToggle] = useState(false)

  const handleAttachmentClick = (fileName: string) => {
    const url = getAssetLink({ file: fileName, path: 'chitown-images/notification' })
    // `noopener` in the features string forces window.open to return null even on success,
    // so sever the opener reference manually instead to keep the return value meaningful.
    const newTab = window.open(url, '_blank')
    if (newTab) {
      newTab.opener = null
    } else {
      toastFactory.error(t('communication:announcementDetails.panel.attachmentPopupBlocked'))
    }
  }

  const isEmail = data.type === CommunicationTypeEnum.enum.Email

  const fields = useMemo(() => getAnnouncementDetailsPanelFields(data, t), [data, t])

  return (
    <S.Card>
      <S.InfoGrid>
        {fields.map(({ label, value, icon }) => (
          <S.InfoField key={label}>
            <Icon icon={icon} size="2rem" color="black100" />
            <S.InfoFieldText>
              <Typography color="black800" size="text-small" weight="semibold">
                {label}
              </Typography>
              <Typography color="black500" size="text-small" weight="medium">
                {value}
              </Typography>
            </S.InfoFieldText>
          </S.InfoField>
        ))}
      </S.InfoGrid>

      <Divider light />
      <S.BodySection>
        <Typography color="black600" size="text-small" weight="bold">
          {t('communication:announcementDetails.panel.subject')}
        </Typography>
        <Typography color="black500" size="text-small" weight="medium">
          {data.subject || EMPTY_VALUE}
        </Typography>
        <Typography color="black600" size="text-small" weight="bold">
          {t('communication:announcementDetails.panel.body')}
        </Typography>
        <Collapse
          in={isBodyExpanded}
          collapsedSize={10}
          watch={[data.body]}
          onOverflowChange={setShowBodyToggle}
        >
          <Typography
            as={'div'}
            color="black500"
            size="text-small"
            weight="medium"
            {...(isEmail && { dangerouslySetInnerHTML: { __html: data.body } })}
          >
            {isEmail ? null : data.body || EMPTY_VALUE}
          </Typography>
        </Collapse>
        {data.image && (
          <S.AnnouncementImage
            src={data.image}
            alt={t('communication:announcementDetails.panel.imageAlt')}
          />
        )}
        {!!data.attachments?.length && (
          <S.AttachmentList>
            {data.attachments.map(({ fileName, originalName }) => (
              <FileItem
                key={fileName}
                fileName={originalName ?? fileName}
                fileType={getFileType(fileName)}
                onClick={() => handleAttachmentClick(fileName)}
              />
            ))}
          </S.AttachmentList>
        )}
        {showBodyToggle && (
          <S.ShowMoreButton onClick={() => setIsBodyExpanded((prev) => !prev)}>
            {isBodyExpanded
              ? t('communication:announcementDetails.panel.showLess')
              : t('communication:announcementDetails.panel.showMore')}
          </S.ShowMoreButton>
        )}
      </S.BodySection>
    </S.Card>
  )
}
